"use client";

import { ChevronDownIcon } from "lucide-react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ComponentProps, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { imagineStore } from "@/stores/imagine.store";
import { createSandboxClient } from "@imagine/sandbox-instrumentation/client";
import {
  type MessagePayload,
  type NetworkRequestData,
  type ConsoleLogMessage,
} from "@imagine/sandbox-instrumentation";
import { PromptAddonType } from "@/stores/slices/prompt-context.slice";
import { trackAnalyticsEvent } from "@/lib/analytics";

export type WebPreviewContextValue = {
  url: string;
  setUrl: (url: string) => void;
  consoleOpen: boolean;
  setConsoleOpen: (open: boolean) => void;
};

const WebPreviewContext = createContext<WebPreviewContextValue | null>(null);

const useWebPreview = () => {
  const context = useContext(WebPreviewContext);
  if (!context) {
    throw new Error("WebPreview components must be used within a WebPreview");
  }
  return context;
};

export type WebPreviewProps = ComponentProps<"div"> & {
  defaultUrl?: string;
  onUrlChange?: (url: string) => void;
};

export const WebPreview = ({
  className,
  children,
  defaultUrl = "",
  onUrlChange,
  ...props
}: WebPreviewProps) => {
  const [url, setUrl] = useState(defaultUrl);
  const [consoleOpen, setConsoleOpen] = useState(false);

  const handleUrlChange = (newUrl: string) => {
    setUrl(newUrl);
    onUrlChange?.(newUrl);
  };

  const contextValue: WebPreviewContextValue = {
    url,
    setUrl: handleUrlChange,
    consoleOpen,
    setConsoleOpen,
  };

  return (
    <WebPreviewContext.Provider value={contextValue}>
      <div
        className={cn("flex size-full flex-col rounded-lg border", className)}
        {...props}
      >
        {children}
      </div>
    </WebPreviewContext.Provider>
  );
};

export type WebPreviewNavigationProps = ComponentProps<"div">;

export const WebPreviewNavigation = ({
  className,
  children,
  ...props
}: WebPreviewNavigationProps) => (
  <div
    className={cn("flex items-center gap-1 border-b p-2", className)}
    {...props}
  >
    {children}
  </div>
);

export type WebPreviewNavigationButtonProps = ComponentProps<typeof Button> & {
  tooltip?: string;
};

export const WebPreviewNavigationButton = ({
  onClick,
  disabled,
  tooltip,
  children,
  ...props
}: WebPreviewNavigationButtonProps) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          className="h-8 w-8 p-0 hover:text-foreground"
          disabled={disabled}
          onClick={onClick}
          size="sm"
          variant="ghost"
          {...props}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export type WebPreviewUrlProps = ComponentProps<typeof Input>;

export const WebPreviewUrl = ({
  value,
  onChange,
  onKeyDown,
  ...props
}: WebPreviewUrlProps) => {
  const { url, setUrl } = useWebPreview();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      const target = event.target as HTMLInputElement;
      setUrl(target.value);
    }
    onKeyDown?.(event);
  };

  return (
    <Input
      className="h-8 flex-1 text-sm"
      onChange={onChange}
      onKeyDown={handleKeyDown}
      placeholder="Enter URL..."
      value={value ?? url}
      {...props}
    />
  );
};

export type WebPreviewBodyProps = ComponentProps<"iframe"> & {
  loading?: ReactNode;
  onLoad?: (e: React.SyntheticEvent<HTMLIFrameElement, Event>) => void;
};

export const WebPreviewBody = ({
  className,
  loading,
  onLoad,
  ...props
}: WebPreviewBodyProps) => {
  const previewUrlWithPreviewToken =
    imagineStore.use.previewState.previewUrlWithPreviewToken();
  const randomGenerationKey =
    imagineStore.use.previewState.randomGenerationKey();
  const sandboxClient = imagineStore.use.sandboxState.sandboxClient();
  const setSandboxClient = imagineStore.use.sandboxState.setSandboxClient();
  const setSandboxInstrumented =
    imagineStore.use.sandboxState.setInstrumented();
  const resetConsoleLogs = imagineStore.use.consoleLogs.reset();
  const resetNetworkLogs = imagineStore.use.networkLogs.reset();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const appendConsoleLog = imagineStore.use.consoleLogs.appendLog();
  const appendNetworkLog = imagineStore.use.networkLogs.append();
  const mergeHistory = imagineStore.use.consoleLogs.mergeHistory();
  const setElementSelectorEnabled =
    imagineStore.use.previewState.setElementSelectorEnabled();
  const addAddon = imagineStore.use.promptContext.addAddon();

  useEffect(() => {
    // console.log("===== WEB PREVIEW =====");
    // console.table({
    //   previewUrlWithPreviewToken: Boolean(previewUrlWithPreviewToken),
    //   "!sandboxClient": sandboxClient === null,
    //   "iframeRef.current": Boolean(iframeRef.current),
    // });
    // console.log("=======================");
    if (previewUrlWithPreviewToken && iframeRef.current) {
      const client = createSandboxClient({
        iframe: () => iframeRef.current,
        targetOrigin: "*",
        allowedOrigins: [previewUrlWithPreviewToken.origin],
      });

      console.log("Sandbox client created");

      client.on("SANDBOX_INSTRUMENTATION_INITIALIZED", () => {
        console.log("SANDBOX_INSTRUMENTATION_INITIALIZED event received");
        console.log("SANDBOX_INSTRUMENTATION_INITIALIZED");
        setSandboxInstrumented(true);
        resetConsoleLogs();
        resetNetworkLogs();
      });

      const unsubscribeLog = client.on("CONSOLE_OUTPUT", (msg) => {
        if (!isConsoleLogMessage(msg)) return;
        appendConsoleLog(msg);
      });

      const unsubscribeHistory = client.on("RESPONSE_LOG_HISTORY", (msg) => {
        if (!Array.isArray(msg.payload)) return;
        const history = msg.payload.filter(
          (entry): entry is ConsoleLogMessage => isConsoleLogMessage(entry),
        );
        if (!history.length) return;
        mergeHistory(history);
      });

      const unsubscribeNetwork = client.on("NETWORK_REQUEST", (msg) => {
        if (!isNetworkRequestPayload(msg)) return;
        const request = msg.request ?? {};
        const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        appendNetworkLog({ id, request });
      });

      const unsubscribeElementSelector = client.on("ELEMENT_CLICKED", (msg) => {
        console.log("ELEMENT_CLICKED", msg);

        const isMultiSelect = msg.isMultiSelect || false;

        // Only disable selector if not multi-selecting
        if (!isMultiSelect) {
          setElementSelectorEnabled(false);
        }

        const payload = msg.payload as {
          xpath: string;
          uniqueId: string;
          pathname: string;
          previewText?: string;
          tagName?: string;
          elementId?: string;
          className?: string;
        };

        if (payload?.xpath && payload?.uniqueId) {
          addAddon(PromptAddonType.SELECTED_ELEMENT, {
            xpath: payload.xpath,
            uniqueId: payload.uniqueId,
            pathname: payload.pathname,
            previewText: payload.previewText,
            tagName: payload.tagName,
            elementId: payload.elementId,
            className: payload.className,
            removable: true,
            // Don't use singleton - allow multiple elements
          });
          void trackAnalyticsEvent("canvas.element_selected", {
            projectId: imagineStore.getState().projectId ?? undefined,
            elementId: payload.elementId,
            path: payload.pathname,
          });
        }
      });

      setSandboxClient(client);

      return () => {
        unsubscribeLog();
        unsubscribeHistory();
        unsubscribeNetwork();
        unsubscribeElementSelector();
        client.destroy();
        setSandboxClient(null);
      };
    }
  }, [iframeRef, setSandboxClient, previewUrlWithPreviewToken]);

  useEffect(() => {
    if (!sandboxClient) return;
  }, [sandboxClient]);

  return (
    <>
      <iframe
        key={randomGenerationKey}
        className={cn("size-full bg-background", className)}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation allow-modals allow-downloads"
        allow="clipboard-write"
        src={previewUrlWithPreviewToken?.toString()}
        title="Preview"
        ref={iframeRef}
        onLoad={onLoad}
        {...props}
      />
      {loading}
    </>
  );
};

export type WebPreviewConsoleProps = ComponentProps<"div"> & {
  logs?: Array<{
    level: "log" | "warn" | "error";
    message: string;
    timestamp: Date;
  }>;
};

export const WebPreviewConsole = ({
  className,
  logs = [],
  children,
  ...props
}: WebPreviewConsoleProps) => {
  const { consoleOpen, setConsoleOpen } = useWebPreview();

  return (
    <Collapsible
      className={cn("border-t bg-muted/50 font-mono text-sm", className)}
      onOpenChange={setConsoleOpen}
      open={consoleOpen}
      {...props}
    >
      <CollapsibleTrigger asChild>
        <Button
          className="flex w-full items-center justify-between p-4 text-left font-medium hover:bg-muted/50"
          variant="ghost"
        >
          Console
          <ChevronDownIcon
            className={cn(
              "h-4 w-4 transition-transform duration-200",
              consoleOpen && "rotate-180",
            )}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent
        className={cn(
          "px-4 pb-4",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 outline-none data-[state=closed]:animate-out data-[state=open]:animate-in",
        )}
      >
        <div className="max-h-48 space-y-1 overflow-y-auto">
          {logs.length === 0 ? (
            <p className="text-muted-foreground">No console output</p>
          ) : (
            logs.map((log, index) => (
              <div
                className={cn(
                  "text-xs",
                  log.level === "error" && "text-destructive",
                  log.level === "warn" && "text-yellow-600",
                  log.level === "log" && "text-foreground",
                )}
                key={`${log.timestamp.getTime()}-${index}`}
              >
                <span className="text-muted-foreground">
                  {log.timestamp.toLocaleTimeString()}
                </span>{" "}
                {log.message}
              </div>
            ))
          )}
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const isConsoleLogMessage = (value: unknown): value is ConsoleLogMessage => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<ConsoleLogMessage>;
  return (
    candidate.type === "CONSOLE_OUTPUT" &&
    typeof candidate.id === "string" &&
    typeof candidate.logged_at === "string" &&
    Array.isArray(candidate.raw) &&
    Array.isArray(candidate.serializedArgs)
  );
};

const isNetworkRequestPayload = (
  payload: MessagePayload,
): payload is MessagePayload & { request: Partial<NetworkRequestData> } => {
  if (!payload || typeof payload !== "object") return false;
  if (payload.type !== "NETWORK_REQUEST") return false;
  return payload.request !== undefined;
};
